import { useCallback, useRef, useEffect, useMemo } from 'react';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { point, bearing, destination, distance as turfDistance, nearestPointOnLine, lineString } from '@turf/turf';

/**
 * Хук для управления инструментами рисования на карте.
 * Инкапсулирует логику работы с MapboxDraw, включая кастомную логику
 * для привязки к углам при рисовании.
 *
 * @param {object} map - Экземпляр карты MapLibre.
 * @param {object} options - Опции.
 * @param {function} options.onFeatureCreate - Колбэк при создании нового объекта.
 * @param {function} options.onFeatureUpdate - Колбэк при обновлении геометрии существующего объекта.
 */
export const useDrawingTools = (map, { onFeatureCreate, onFeatureUpdate, onFeatureSelect, onFeatureCancelEdit, onToolDeactivate }) => {
   const drawRef = useRef(null);
   const onDrawCompleteCallbackRef = useRef(null);
   const currentDrawTypeRef = useRef(null);
   const isGeometryEditRef = useRef(false);
   const onFeatureCreateRef = useRef(onFeatureCreate);
   const onFeatureUpdateRef = useRef(onFeatureUpdate);
   const onFeatureSelectRef = useRef(onFeatureSelect);
   const onFeatureCancelEditRef = useRef(onFeatureCancelEdit);
   const onToolDeactivateRef = useRef(onToolDeactivate);
   const editingFeatureTargetRef = useRef(null);

   // История вертексов для отката действий внутри сессии редактирования геометрии
   const vertexHistoryRef = useRef([]);
   const vertexHistoryIndexRef = useRef(-1);
   const lastClickTimeRef = useRef(0);

   const pushVertexSnapshotRef = useRef(null);
   const undoVertexRef = useRef(null);
   const redoVertexRef = useRef(null);
   const undoLastPointRef = useRef(null);

   const pushVertexSnapshot = useCallback((coords) => {
       if (!coords) return;
       const jsonCoords = JSON.stringify(coords);
       const history = vertexHistoryRef.current;
       const currentIndex = vertexHistoryIndexRef.current;

       if (currentIndex >= 0 && JSON.stringify(history[currentIndex]) === jsonCoords) {
           return;
       }

       const newHistory = history.slice(0, currentIndex + 1);
       newHistory.push(JSON.parse(jsonCoords));
       if (newHistory.length > 50) newHistory.shift();

       vertexHistoryRef.current = newHistory;
       vertexHistoryIndexRef.current = newHistory.length - 1;
   }, []);

    const undoVertex = useCallback(() => {
        if (!drawRef.current || vertexHistoryIndexRef.current <= 0) return false;
        vertexHistoryIndexRef.current--;
        const coords = vertexHistoryRef.current[vertexHistoryIndexRef.current];
        const all = drawRef.current.getAll();
        if (all && all.features && all.features[0]) {
            const feat = all.features[0];
            const updatedFeat = {
                ...feat,
                geometry: {
                    ...feat.geometry,
                    coordinates: coords
                }
            };
            drawRef.current.add(updatedFeat);
            try {
                drawRef.current.changeMode('direct_select', { featureId: feat.id });
            } catch (e) {}
            return true;
        }
        return false;
    }, []);

    const redoVertex = useCallback(() => {
        if (!drawRef.current || vertexHistoryIndexRef.current >= vertexHistoryRef.current.length - 1) return false;
        vertexHistoryIndexRef.current++;
        const coords = vertexHistoryRef.current[vertexHistoryIndexRef.current];
        const all = drawRef.current.getAll();
        if (all && all.features && all.features[0]) {
            const feat = all.features[0];
            const updatedFeat = {
                ...feat,
                geometry: {
                    ...feat.geometry,
                    coordinates: coords
                }
            };
            drawRef.current.add(updatedFeat);
            try {
                drawRef.current.changeMode('direct_select', { featureId: feat.id });
            } catch (e) {}
            return true;
        }
        return false;
    }, []);

    pushVertexSnapshotRef.current = pushVertexSnapshot;
    undoVertexRef.current = undoVertex;
    redoVertexRef.current = redoVertex;

   useEffect(() => {
       onFeatureCreateRef.current = onFeatureCreate;
   }, [onFeatureCreate]);

   useEffect(() => {
       onFeatureUpdateRef.current = onFeatureUpdate;
   }, [onFeatureUpdate]);

   useEffect(() => {
       onFeatureSelectRef.current = onFeatureSelect;
   }, [onFeatureSelect]);

   useEffect(() => {
       onFeatureCancelEditRef.current = onFeatureCancelEdit;
   }, [onFeatureCancelEdit]);

   useEffect(() => {
       onToolDeactivateRef.current = onToolDeactivate;
   }, [onToolDeactivate]);

  // --- Логика для привязки к углам ---

  // Функция для вычисления географического азимута между двумя точками
  const calculateBearing = (p1, p2) => {
    return bearing(p1, p2);
  }

  // Функция для привязки угла к ближайшему значению, кратному 45 градусам
  const snapAngle = (angle) => {
    const snapAngle = 45;
    return Math.round(angle / snapAngle) * snapAngle;
  };

  const currentDrawModeInstanceRef = useRef(null);

  const undoLastPoint = useCallback(() => {
    const inst = currentDrawModeInstanceRef.current;
    if (!inst) return false;

    const { type, ctx, state, opts } = inst;

    if (type === 'polygon' && state.polygon) {
      const coords = state.polygon.coordinates?.[0] || [];
      // Если только 1 точка или меньше (с учетом перемещаемой точки под курсором)
      if (state.currentVertexPosition <= 1 || coords.length <= 2) {
        try {
          ctx.deleteFeature([state.polygon.id], { silent: true });
        } catch (e) {}
        ctx.changeMode('draw_polygon_with_angle_snap', opts || {});
        return true;
      }

      // Если 2 или более поставленных точек
      try {
        const cursorCoord = coords[state.currentVertexPosition] || coords[coords.length - 1];
        const newRing = [
          ...coords.slice(0, state.currentVertexPosition - 1),
          cursorCoord
        ];
        state.polygon.coordinates = [newRing];
        state.currentVertexPosition--;
        if (state.polygon.changed) {
          state.polygon.changed();
        }
        return true;
      } catch (err) {
        console.warn('Polygon point undo error:', err);
      }
    } else if (type === 'line' && state.line) {
      const coords = state.line.coordinates || [];
      if (state.currentVertexPosition <= 1 || coords.length <= 2) {
        try {
          ctx.deleteFeature([state.line.id], { silent: true });
        } catch (e) {}
        ctx.changeMode('draw_line_string_with_angle_snap', opts || {});
        return true;
      }

      try {
        const cursorCoord = coords[state.currentVertexPosition] || coords[coords.length - 1];
        const newCoords = [
          ...coords.slice(0, state.currentVertexPosition - 1),
          cursorCoord
        ];
        state.line.coordinates = newCoords;
        state.currentVertexPosition--;
        if (state.line.changed) {
          state.line.changed();
        }
        return true;
      } catch (err) {
        console.warn('Line point undo error:', err);
      }
    }
    return false;
  }, []);

   undoLastPointRef.current = undoLastPoint;

  // Кастомный режим для рисования линий с привязкой
  const drawLineStringWithAngleSnap = useMemo(() => {
    return {
      ...MapboxDraw.modes.draw_line_string,
      onSetup(opts) {
        const state = MapboxDraw.modes.draw_line_string.onSetup.call(this, opts);
        currentDrawModeInstanceRef.current = {
          type: 'line',
          ctx: this,
          state,
          opts
        };
        return state;
      },
      onStop(state) {
        if (currentDrawModeInstanceRef.current?.state === state) {
          currentDrawModeInstanceRef.current = null;
        }
        if (MapboxDraw.modes.draw_line_string.onStop) {
          MapboxDraw.modes.draw_line_string.onStop.call(this, state);
        }
      },
      onTrash(state) {
        undoLastPointRef.current?.();
      },
      onKeyDown(state, e) {
        const isUndoKey = e.keyCode === 8 || e.keyCode === 46 ||
          ((e.ctrlKey || e.metaKey) && (e.code === 'KeyZ' || e.key?.toLowerCase() === 'z' || e.key === 'я'));

        if (isUndoKey) {
          e.stopPropagation();
          if (e.preventDefault) e.preventDefault();
          undoLastPointRef.current?.();
          return;
        }

        if (MapboxDraw.modes.draw_line_string.onKeyDown) {
          MapboxDraw.modes.draw_line_string.onKeyDown.call(this, state, e);
        }
      },
      onMouseMove(state, e) {
        // Вызываем оригинальный обработчик
        MapboxDraw.modes.draw_line_string.onMouseMove.call(this, state, e);

        if (!state.line || !e.originalEvent.shiftKey) return;

        const currentCoordinates = state.line.coordinates;
        if (currentCoordinates.length < 2) return;

        const prevPointCoords = currentCoordinates[currentCoordinates.length - 2];
        const prevPoint = point(prevPointCoords);
        const currentPoint = point([e.lngLat.lng, e.lngLat.lat]);

        let finalAngle;

        // Если это второй сегмент или более, считаем угол относительно предыдущего сегмента
        if (currentCoordinates.length > 2) {
          const p0 = point(currentCoordinates[currentCoordinates.length - 3]); // Точка перед предыдущей
          const p1 = prevPoint; // Предыдущая точка
          const p2 = currentPoint; // Текущая точка

          const bearingPrev = calculateBearing(p0, p1);
          const bearingCurr = calculateBearing(p1, p2);
          
          let relativeAngle = bearingCurr - bearingPrev;
          // Нормализуем угол в диапазон (-180, 180]
          if (relativeAngle <= -180) relativeAngle += 360;
          if (relativeAngle > 180) relativeAngle -= 360;

          const snappedRelativeAngle = snapAngle(relativeAngle);
          finalAngle = bearingPrev + snappedRelativeAngle;
        } else { // Для первого сегмента привязываемся к абсолютным углам (N, E, S, W)
          finalAngle = snapAngle(calculateBearing(prevPoint, currentPoint));
        }

        const distanceInKm = turfDistance(prevPoint, currentPoint, { units: 'kilometers' });
        const newCoords = destination(prevPoint, distanceInKm, finalAngle, { units: 'kilometers' }).geometry.coordinates;

        // Обновляем последнюю координату линии
        state.line.updateCoordinate(currentCoordinates.length - 1, newCoords[0], newCoords[1]);
      },
      onClick(state, e) {
        // Применяем логику привязки и при клике
        let snappedCoords = null;
        if (e.originalEvent.shiftKey && state.line && state.line.coordinates.length > 1) {
            const currentCoordinates = state.line.coordinates;

            let finalAngle;
            const p1Coords = currentCoordinates[currentCoordinates.length - 2];
            const p2Coords = [e.lngLat.lng, e.lngLat.lat];

            if (currentCoordinates.length > 2) {
                const p0Coords = currentCoordinates[currentCoordinates.length - 3];
                const bearingPrev = calculateBearing(point(p0Coords), point(p1Coords));
                const bearingCurr = calculateBearing(point(p1Coords), point(p2Coords));
                
                let relativeAngle = bearingCurr - bearingPrev;
                if (relativeAngle <= -180) relativeAngle += 360;
                if (relativeAngle > 180) relativeAngle -= 360;

                const snappedRelativeAngle = snapAngle(relativeAngle);
                finalAngle = bearingPrev + snappedRelativeAngle;
            } else {
                finalAngle = snapAngle(calculateBearing(point(p1Coords), point(p2Coords)));
            }

            const distanceInKm = turfDistance(point(p1Coords), point(p2Coords), { units: 'kilometers' });
            snappedCoords = destination(point(p1Coords), distanceInKm, finalAngle, { units: 'kilometers' }).geometry.coordinates;

            // Обновляем координаты в событии перед вызовом стандартного обработчика
            e.lngLat = { lng: snappedCoords[0], lat: snappedCoords[1] };
            state.line.updateCoordinate(currentCoordinates.length - 1, snappedCoords[0], snappedCoords[1]);
        }

        // Вызываем оригинальный обработчик клика
        MapboxDraw.modes.draw_line_string.onClick.call(this, state, e);

        // Гарантируем точную фиксацию точки в snappedCoords
        if (snappedCoords && state.line) {
            const lastIdx = state.currentVertexPosition - 1;
            if (lastIdx >= 0) {
                state.line.updateCoordinate(lastIdx, snappedCoords[0], snappedCoords[1]);
            }
        }
      },
      onKeyUp(state, e) {
        // Завершаем рисование по Enter
        if (e.keyCode === 13 && state.line && state.line.isValid()) {
            this.changeMode('simple_select', { featureIds: [state.line.id] });
            return;
        }
        if (e.keyCode === 27) {
            return;
        }
        if (MapboxDraw.modes.draw_line_string.onKeyUp) {
            MapboxDraw.modes.draw_line_string.onKeyUp.call(this, state, e);
        }
      },
      toDisplayFeatures(state, geojson, display) {
        const coords = geojson.geometry && geojson.geometry.coordinates;
        if (!coords || !Array.isArray(coords) || coords.length < 2 || !coords[0] || coords[0][0] === undefined) {
            return;
        }

        return MapboxDraw.modes.draw_line_string.toDisplayFeatures.call(this, state, geojson, display);
      }
    };
  }, []);

  // Кастомный режим для рисования полигонов с привязкой
  const drawPolygonWithAngleSnap = useMemo(() => {
    return {
      ...MapboxDraw.modes.draw_polygon,
      onSetup(opts) {
        const state = MapboxDraw.modes.draw_polygon.onSetup.call(this, opts);
        currentDrawModeInstanceRef.current = {
          type: 'polygon',
          ctx: this,
          state,
          opts
        };
        return state;
      },
      onStop(state) {
        if (currentDrawModeInstanceRef.current?.state === state) {
          currentDrawModeInstanceRef.current = null;
        }
        if (MapboxDraw.modes.draw_polygon.onStop) {
          MapboxDraw.modes.draw_polygon.onStop.call(this, state);
        }
      },
      onTrash(state) {
        undoLastPointRef.current?.();
      },
      onKeyDown(state, e) {
        const isUndoKey = e.keyCode === 8 || e.keyCode === 46 ||
          ((e.ctrlKey || e.metaKey) && (e.code === 'KeyZ' || e.key?.toLowerCase() === 'z' || e.key === 'я'));

        if (isUndoKey) {
          e.stopPropagation();
          if (e.preventDefault) e.preventDefault();
          undoLastPointRef.current?.();
          return;
        }

        if (MapboxDraw.modes.draw_polygon.onKeyDown) {
          MapboxDraw.modes.draw_polygon.onKeyDown.call(this, state, e);
        }
      },
      onMouseMove(state, e) {
        // Вызываем оригинальный обработчик
        MapboxDraw.modes.draw_polygon.onMouseMove.call(this, state, e);

        if (!state.polygon || !e.originalEvent.shiftKey) return;

        const currentCoordinates = state.polygon.coordinates[0];
        if (currentCoordinates.length < 2) return;

        const prevPointCoords = currentCoordinates[currentCoordinates.length - 2];
        const prevPoint = point(prevPointCoords);
        const currentPoint = point([e.lngLat.lng, e.lngLat.lat]);

        // Специальная привязка 4-й точки (двойной прямой угол 90° к обеим сторонам p2 и p0)
        if (currentCoordinates.length === 4) {
            const p0Coords = currentCoordinates[0];
            const p1Coords = currentCoordinates[1];
            const p2Coords = currentCoordinates[2];

            const p0Pt = point(p0Coords);
            const p1Pt = point(p1Coords);
            const p2Pt = point(p2Coords);

            // Вектор p1 -> p0 переносим в p2 для образования прямоугольника
            const dist10 = turfDistance(p1Pt, p0Pt, { units: 'kilometers' });
            const bearing10 = calculateBearing(p1Pt, p0Pt);
            const rectCorner = destination(p2Pt, dist10, bearing10, { units: 'kilometers' }).geometry.coordinates;

            const distToRectCorner = turfDistance(currentPoint, point(rectCorner), { units: 'kilometers' });
            const diagDist = turfDistance(p0Pt, p2Pt, { units: 'kilometers' });

            // Если курсор находится в секторе 4-го угла (в пределах зоны притяжения)
            if (diagDist > 0 && (distToRectCorner / diagDist) < 0.45) {
                state.polygon.updateCoordinate(`0.${currentCoordinates.length - 1}`, rectCorner[0], rectCorner[1]);
                return;
            }
        }

        let finalAngle;

        // Если это второй сегмент или более, считаем угол относительно предыдущего сегмента
        if (currentCoordinates.length > 2) {
            const p0 = point(currentCoordinates[currentCoordinates.length - 3]);
            const p1 = prevPoint;
            const p2 = currentPoint;

            const bearingPrev = calculateBearing(p0, p1);
            const bearingCurr = calculateBearing(p1, p2);
            let relativeAngle = bearingCurr - bearingPrev;
            if (relativeAngle <= -180) relativeAngle += 360;
            if (relativeAngle > 180) relativeAngle -= 360;

            const snappedRelativeAngle = snapAngle(relativeAngle);
            finalAngle = bearingPrev + snappedRelativeAngle;
        } else { // Для первого сегмента привязываемся к абсолютным углам
            finalAngle = snapAngle(calculateBearing(prevPoint, currentPoint));
        }
        const distanceInKm = turfDistance(prevPoint, currentPoint, { units: 'kilometers' });
        const newCoords = destination(prevPoint, distanceInKm, finalAngle, { units: 'kilometers' }).geometry.coordinates;

        // Обновляем последнюю координату полигона
        state.polygon.updateCoordinate(`0.${currentCoordinates.length - 1}`, newCoords[0], newCoords[1]);
      },
      onClick(state, e) {
        // Применяем логику привязки и при клике
        let snappedCoords = null;
        if (e.originalEvent.shiftKey && state.polygon && state.polygon.coordinates[0].length > 1) {
            const currentCoordinates = state.polygon.coordinates[0];

            // Проверка привязки 4-й точки при клике (замыкание прямоугольника к обеим сторонам)
            if (currentCoordinates.length === 4) {
                const p0Pt = point(currentCoordinates[0]);
                const p1Pt = point(currentCoordinates[1]);
                const p2Pt = point(currentCoordinates[2]);
                const currentPoint = point([e.lngLat.lng, e.lngLat.lat]);

                const dist10 = turfDistance(p1Pt, p0Pt, { units: 'kilometers' });
                const bearing10 = calculateBearing(p1Pt, p0Pt);
                const rectCorner = destination(p2Pt, dist10, bearing10, { units: 'kilometers' }).geometry.coordinates;

                const distToRectCorner = turfDistance(currentPoint, point(rectCorner), { units: 'kilometers' });
                const diagDist = turfDistance(p0Pt, p2Pt, { units: 'kilometers' });

                if (diagDist > 0 && (distToRectCorner / diagDist) < 0.45) {
                    snappedCoords = rectCorner;
                } else {
                    let finalAngle;
                    if (currentCoordinates.length > 2) {
                        const p0 = point(currentCoordinates[currentCoordinates.length - 3]);
                        const p1 = p2Pt;
                        const p2 = currentPoint;
                        const bearingPrev = calculateBearing(p0, p1);
                        const bearingCurr = calculateBearing(p1, p2);
                        let relativeAngle = bearingCurr - bearingPrev;
                        if (relativeAngle <= -180) relativeAngle += 360;
                        if (relativeAngle > 180) relativeAngle -= 360;
                        const snappedRelativeAngle = snapAngle(relativeAngle);
                        finalAngle = bearingPrev + snappedRelativeAngle;
                    } else {
                        finalAngle = snapAngle(calculateBearing(p2Pt, currentPoint));
                    }
                    const distanceInKm = turfDistance(p2Pt, currentPoint, { units: 'kilometers' });
                    snappedCoords = destination(p2Pt, distanceInKm, finalAngle, { units: 'kilometers' }).geometry.coordinates;
                }
            } else {
                let finalAngle;
                const p1Coords = currentCoordinates[currentCoordinates.length - 2];
                const p2Coords = [e.lngLat.lng, e.lngLat.lat];

                if (currentCoordinates.length > 2) {
                    const p0Coords = currentCoordinates[currentCoordinates.length - 3];
                    const bearingPrev = calculateBearing(point(p0Coords), point(p1Coords));
                    const bearingCurr = calculateBearing(point(p1Coords), point(p2Coords));
                    
                    let relativeAngle = bearingCurr - bearingPrev;
                    if (relativeAngle <= -180) relativeAngle += 360;
                    if (relativeAngle > 180) relativeAngle -= 360;

                    const snappedRelativeAngle = snapAngle(relativeAngle);
                    finalAngle = bearingPrev + snappedRelativeAngle;
                } else {
                    finalAngle = snapAngle(calculateBearing(point(p1Coords), point(p2Coords)));
                }

                const distanceInKm = turfDistance(point(p1Coords), point(p2Coords), { units: 'kilometers' });
                snappedCoords = destination(point(p1Coords), distanceInKm, finalAngle, { units: 'kilometers' }).geometry.coordinates;
            }

            if (snappedCoords) {
                // Обновляем координаты в событии перед вызовом стандартного обработчика
                e.lngLat = { lng: snappedCoords[0], lat: snappedCoords[1] };
                state.polygon.updateCoordinate(`0.${currentCoordinates.length - 1}`, snappedCoords[0], snappedCoords[1]);
            }
        }

        // Вызываем оригинальный обработчик клика MapboxDraw
        MapboxDraw.modes.draw_polygon.onClick.call(this, state, e);

        // Гарантируем точную фиксацию точки в snappedCoords
        if (snappedCoords && state.polygon) {
            const lastIdx = state.currentVertexPosition - 1;
            if (lastIdx >= 0) {
                state.polygon.updateCoordinate(`0.${lastIdx}`, snappedCoords[0], snappedCoords[1]);
            }
        }
      },
      onKeyUp(state, e) {
        // Завершаем рисование по Enter
        if (e.keyCode === 13 && state.polygon && state.polygon.isValid()) {
            this.changeMode('simple_select', { featureIds: [state.polygon.id] });
            return;
        }
        if (e.keyCode === 27) {
            return;
        }
        if (MapboxDraw.modes.draw_polygon.onKeyUp) {
            MapboxDraw.modes.draw_polygon.onKeyUp.call(this, state, e);
        }
      },
      toDisplayFeatures(state, geojson, display) {
        const coords = geojson.geometry && geojson.geometry.coordinates && geojson.geometry.coordinates[0];
        if (!coords || !Array.isArray(coords) || coords.length < 2 || !coords[0] || coords[0][0] === undefined) {
            return;
        }

        return MapboxDraw.modes.draw_polygon.toDisplayFeatures.call(this, state, geojson, display);
      }
    };
  }, []);

    // Кастомный режим для рисования точек
    const drawPointCustom = useMemo(() => {
      const originalDrawPoint = MapboxDraw.modes.draw_point;
      
      return {
        ...originalDrawPoint,
        
        onSetup(state) {
          const point = this.newFeature({
            type: 'Feature',
            properties: state.properties || {},
            geometry: { type: 'Point', coordinates: [] }
          });
          this.addFeature(point);
          this.clearSelectedFeatures();
          this.updateUIClasses({ mouse: 'add' });
          this.activateUIButton('point');
          this.setActionableState({ trash: true });
          return { point };
        },

        onClick(state, e) {
          e.originalEvent.stopPropagation();
          state.point.updateCoordinate(e.lngLat.lng, e.lngLat.lat);
          return originalDrawPoint.onClick.call(this, state, e);
        },
      };
    }, []);

    // Кастомный режим прямого редактирования вершин с привязкой углов, вставкой вершин по клику и защитой от случайного сдвига
    const directSelectCustom = useMemo(() => {
      const originalDirectSelect = MapboxDraw.modes.direct_select;

      return {
        ...originalDirectSelect,

        onDrag(state, e) {
          // Защита: если не выбрана конкретная вершина (попытка перетащить весь полигон целиком) — блокируем перемещение всего объекта
          if (!state.selectedCoordPaths || state.selectedCoordPaths.length === 0) {
            return;
          }

          // Привязка к углам 45°/90° при перетаскивании вершины с зажатым Shift
          if (state.selectedCoordPaths.length > 0 && e.originalEvent.shiftKey) {
            const coordPath = state.selectedCoordPaths[0];
            const feature = state.feature;

            if (feature && feature.coordinates) {
              const isPolygon = feature.type === 'Polygon';
              const coords = isPolygon ? feature.coordinates[0] : feature.coordinates;
              const parts = coordPath.split('.');
              const vertexIndex = parseInt(parts[parts.length - 1], 10);

              if (!isNaN(vertexIndex) && coords && coords.length > 1) {
                const numPoints = isPolygon ? coords.length - 1 : coords.length;
                const prevIndex = (vertexIndex - 1 + numPoints) % numPoints;
                const prevPointCoords = coords[prevIndex];

                if (prevPointCoords) {
                  const prevPt = point(prevPointCoords);
                  const currentPt = point([e.lngLat.lng, e.lngLat.lat]);
                  const rawBearing = calculateBearing(prevPt, currentPt);
                  const snappedAngle = snapAngle(rawBearing);
                  const distKm = turfDistance(prevPt, currentPt, { units: 'kilometers' });
                  const snapped = destination(prevPt, distKm, snappedAngle, { units: 'kilometers' }).geometry.coordinates;

                  e.lngLat = { lng: snapped[0], lat: snapped[1] };
                }
              }
            }
          }

          return originalDirectSelect.onDrag.call(this, state, e);
        },

        onMouseUp(state, e) {
          const res = originalDirectSelect.onMouseUp ? originalDirectSelect.onMouseUp.call(this, state, e) : undefined;
          if (state.feature) {
            pushVertexSnapshotRef.current?.(state.feature.getCoordinates());
          }
          return res;
        },

        onTrash(state) {
          const res = originalDirectSelect.onTrash ? originalDirectSelect.onTrash.call(this, state) : undefined;
          if (state.feature) {
            pushVertexSnapshotRef.current?.(state.feature.getCoordinates());
          }
          return res;
        },

        onMouseMove(state, e) {
          if (originalDirectSelect.onMouseMove) {
            originalDirectSelect.onMouseMove.call(this, state, e);
          }
          // Курсор "move" показываем ТОЛЬКО над вершинами и мидпоинтами, над телом полигона — обычный курсор
          const isOverVertexOrMid = e.featureTarget && (
            e.featureTarget.properties?.meta === 'vertex' ||
            e.featureTarget.properties?.meta === 'midpoint'
          );
          if (!isOverVertexOrMid) {
            this.updateUIClasses({ mouse: 'pointer' });
          }
        },

        onClick(state, e) {
          // 1. Клик по промежуточной точке (midpoint) — создаем вершину
          if (e.featureTarget && e.featureTarget.properties && e.featureTarget.properties.meta === 'midpoint') {
            const res = originalDirectSelect.onClick.call(this, state, e);
            if (state.feature) {
              pushVertexSnapshotRef.current?.(state.feature.getCoordinates());
            }
            return res;
          }

          // 2. Клик по существующей вершине
          if (e.featureTarget && e.featureTarget.properties && e.featureTarget.properties.meta === 'vertex') {
            return originalDirectSelect.onClick.call(this, state, e);
          }

          // 3. Клик или двойной клик по ребру/границе объекта для добавления новой вершины в месте клика
          const now = Date.now();
          const isDoubleClick = (now - lastClickTimeRef.current < 450);
          lastClickTimeRef.current = now;

          const feature = state.feature;
          if (feature && feature.coordinates) {
            const isPolygon = feature.type === 'Polygon';
            const ring = isPolygon ? feature.coordinates[0] : feature.coordinates;

            if (Array.isArray(ring) && ring.length >= 2) {
              const clickPt = point([e.lngLat.lng, e.lngLat.lat]);
              let bestIndex = -1;
              let bestDist = Infinity;
              let bestCoord = null;

              for (let i = 0; i < ring.length - 1; i++) {
                try {
                  const seg = lineString([ring[i], ring[i + 1]]);
                  const nearest = nearestPointOnLine(seg, clickPt);
                  const d = turfDistance(clickPt, nearest, { units: 'kilometers' });
                  if (d < bestDist) {
                    bestDist = d;
                    bestIndex = i;
                    bestCoord = nearest.geometry.coordinates;
                  }
                } catch (err) {}
              }

              // Если клик на границу (в радиусе допуска или двойной клик)
              if (bestIndex !== -1 && (isDoubleClick || bestDist < 0.08)) {
                const newPath = isPolygon ? `0.${bestIndex + 1}` : `${bestIndex + 1}`;
                const insertLng = bestCoord ? bestCoord[0] : e.lngLat.lng;
                const insertLat = bestCoord ? bestCoord[1] : e.lngLat.lat;
                
                feature.addCoordinate(newPath, insertLng, insertLat);
                state.selectedCoordPaths = [newPath];
                feature.changed();
                pushVertexSnapshotRef.current?.(feature.getCoordinates());
                return;
              }
            }
          }

          return originalDirectSelect.onClick.call(this, state, e);
        },

        onKeyDown(state, e) {
          // Откат / повтор по Ctrl+Z / Ctrl+Y
          if ((e.ctrlKey || e.metaKey) && (e.code === 'KeyZ' || e.key.toLowerCase() === 'z' || e.key === 'я')) {
            e.stopPropagation();
            if (e.preventDefault) e.preventDefault();
            if (e.shiftKey) {
              redoVertexRef.current?.();
            } else {
              undoVertexRef.current?.();
            }
            return;
          }

          if ((e.ctrlKey || e.metaKey) && (e.code === 'KeyY' || e.key.toLowerCase() === 'y' || e.key === 'н')) {
            e.stopPropagation();
            if (e.preventDefault) e.preventDefault();
            redoVertexRef.current?.();
            return;
          }

          if (e.keyCode === 27) { // Escape - отмена изменений
            e.stopPropagation();
            if (e.preventDefault) e.preventDefault();
            if (onFeatureCancelEditRef.current) {
              onFeatureCancelEditRef.current();
            }
            if (drawRef.current) {
              drawRef.current.changeMode('simple_select', {});
              drawRef.current.deleteAll();
            }
            return;
          }

          if (e.keyCode === 13) { // Enter - сохранение
            e.stopPropagation();
            if (e.preventDefault) e.preventDefault();
            if (state.feature && state.feature.isValid()) {
              const updated = state.feature.toGeoJSON();
              if (onFeatureUpdateRef.current) {
                onFeatureUpdateRef.current(updated);
              }
            }
            if (drawRef.current) {
              drawRef.current.changeMode('simple_select', {});
              drawRef.current.deleteAll();
            }
            return;
          }

          if (originalDirectSelect.onKeyDown) {
            originalDirectSelect.onKeyDown.call(this, state, e);
            if (state.feature) {
              pushVertexSnapshotRef.current?.(state.feature.getCoordinates());
            }
          }
        }
      };
    }, []);

  // Инициализация MapboxDraw при монтировании
  useEffect(() => {
    if (!map) return;

    const safeRemoveDraw = () => {
      try {
        if (drawRef.current) {
          try {
            map.removeControl(drawRef.current);
          } catch (e) {}
        }
        if (map.getStyle && map.getStyle()) {
          const style = map.getStyle();
          const layers = style?.layers || [];
          layers.forEach(l => {
            if (l.id && (l.id.startsWith('gl-draw-') || l.id.includes('mapbox-gl-draw'))) {
              try { map.removeLayer(l.id); } catch (e) {}
            }
          });
          if (map.getSource && map.getSource('mapbox-gl-draw-cold')) {
            try { map.removeSource('mapbox-gl-draw-cold'); } catch (e) {}
          }
          if (map.getSource && map.getSource('mapbox-gl-draw-hot')) {
            try { map.removeSource('mapbox-gl-draw-hot'); } catch (e) {}
          }
        }
      } catch (err) {
        console.warn('MapboxDraw cleanup warning:', err);
      }
    };

    safeRemoveDraw();

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      modes: {
        ...MapboxDraw.modes,
        'draw_line_string_with_angle_snap': drawLineStringWithAngleSnap,
        'draw_polygon_with_angle_snap': drawPolygonWithAngleSnap,
        'draw_point_custom': drawPointCustom,
        'direct_select': directSelectCustom,
      },
      styles: [
        // 1. Полигон заливка (активный / редактируемый)
        {
          id: 'gl-draw-polygon-fill',
          type: 'fill',
          filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          paint: { 'fill-color': '#0284c7', 'fill-outline-color': '#0284c7', 'fill-opacity': 0.2 },
        },
        // 2. Полигон контур (активный / редактируемый)
        {
          id: 'gl-draw-polygon-stroke-active',
          type: 'line',
          filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#0284c7', 'line-width': 2.5 },
        },
        // 3. Линия (активная / редактируемая)
        {
          id: 'gl-draw-line',
          type: 'line',
          filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#0284c7', 'line-dasharray': [0.2, 2], 'line-width': 2.5 },
        },
        // 4. Промежуточные точки на гранях (midpoints) для добавления вершин кликом
        {
          id: 'gl-draw-polygon-midpoint',
          type: 'circle',
          filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
          paint: {
            'circle-radius': 6,
            'circle-color': '#ffffff',
            'circle-stroke-width': 2.5,
            'circle-stroke-color': '#0284c7',
            'circle-opacity': 0.95
          },
        },
        // 5. Неактивные вершины полигона/линии
        {
          id: 'gl-draw-polygon-and-line-vertex-inactive',
          type: 'circle',
          filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static'], ['!=', 'active', 'true']],
          paint: {
            'circle-radius': 7,
            'circle-color': '#ffffff',
            'circle-stroke-width': 2.5,
            'circle-stroke-color': '#0284c7'
          },
        },
        // 6. Активная (выбранная/перетаскиваемая) вершина
        {
          id: 'gl-draw-polygon-and-line-vertex-active',
          type: 'circle',
          filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static'], ['==', 'active', 'true']],
          paint: {
            'circle-radius': 8.5,
            'circle-color': '#f97316',
            'circle-stroke-width': 3,
            'circle-stroke-color': '#ffffff'
          },
        },
        // 7. Общий fallback для вершин
        {
          id: 'gl-draw-vertex-point-active',
          type: 'circle',
          filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
          paint: { 'circle-radius': 7, 'circle-color': '#ffffff', 'circle-stroke-width': 2.5, 'circle-stroke-color': '#0284c7' },
        },
        // 8. Точечные маркеры
        {
          id: 'gl-draw-point-active',
          type: 'circle',
          filter: ['all', ['==', '$type', 'Point'], ['!=', 'meta', 'vertex'], ['!=', 'meta', 'midpoint'], ['!=', 'mode', 'static']],
          paint: { 'circle-radius': 7, 'circle-color': '#0284c7', 'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff' },
        },
      ],
    });

    try {
      map.addControl(draw);
      drawRef.current = draw;
    } catch (err) {
      console.warn('MapboxDraw initial addControl failed, retrying after cleanup:', err);
      safeRemoveDraw();
      try {
        map.addControl(draw);
        drawRef.current = draw;
      } catch (e2) {
        console.error('MapboxDraw retry failed:', e2);
      }
    }

    MapboxDraw.constants.modes.DRAW_LINE_STRING = 'draw_line_string';
    if (draw.options) {
      draw.options.snap = true;
      draw.options.guides = true;
    }

    const handleCreate = ({ features }) => {
      const isMeasurement = !!onDrawCompleteCallbackRef.current;
      const feature = { ...features[0] };
      feature.properties = { ...feature.properties };

      if (isMeasurement) {
        onDrawCompleteCallbackRef.current(feature);
        return;
      }

      if (isGeometryEditRef.current && editingFeatureTargetRef.current) {
        const original = editingFeatureTargetRef.current;
        const updatedFeature = {
          ...original,
          geometry: feature.geometry,
          properties: {
            ...original.properties,
            type: original.properties?.type || currentDrawTypeRef.current || 'plot'
          }
        };

        // Загружаем обновленный контур обратно в direct_select, чтобы пользователь мог продолжить редактировать вершины или нажать Сохранить
        drawRef.current.deleteAll();
        if (feature.geometry && feature.geometry.coordinates) {
          vertexHistoryRef.current = [JSON.parse(JSON.stringify(feature.geometry.coordinates))];
          vertexHistoryIndexRef.current = 0;
        }
        drawRef.current.add(updatedFeature);
        setTimeout(() => {
          if (drawRef.current) {
            drawRef.current.changeMode('direct_select', { featureId: updatedFeature.id });
          }
        }, 30);
        return;
      }

      if (!feature.id) {
          feature.id = crypto.randomUUID();
      }
      feature.properties.id = feature.id;

      if (currentDrawTypeRef.current && !feature.properties.type) {
          feature.properties.type = currentDrawTypeRef.current;
      }

      if (onFeatureCreateRef.current) {
        onFeatureCreateRef.current(feature);
        if (drawRef.current) {
            drawRef.current.deleteAll();
        }
      }
    };
    
    const handleUpdate = ({ features, action }) => {
        if (action === 'move' || action === 'change_coordinates') {
            const updatedFeature = features[0];
            if (updatedFeature && updatedFeature.geometry) {
              pushVertexSnapshotRef.current?.(updatedFeature.geometry.coordinates);
            }
            if (onFeatureUpdateRef.current && !isGeometryEditRef.current) {
                onFeatureUpdateRef.current(updatedFeature);
            }
        }
    };

    map.on('draw.create', handleCreate);
    map.on('draw.update', handleUpdate);

    return () => {
      try {
        if (map) {
          map.off('draw.create', handleCreate);
          map.off('draw.update', handleUpdate);
          safeRemoveDraw();
        }
      } catch (err) {
        console.warn('MapboxDraw unmount warning:', err);
      }
      drawRef.current = null;
    };
  }, [map, directSelectCustom, drawPointCustom, drawLineStringWithAngleSnap, drawPolygonWithAngleSnap]);

  const currentDrawModeRef = useRef(null);

  const startDrawing = useCallback((mode, defaultProperties, isGeometryEdit = false) => {
    if (!drawRef.current) return;
    
    currentDrawTypeRef.current = defaultProperties?.type || null;
    currentDrawModeRef.current = { mode, defaultProperties };
    isGeometryEditRef.current = isGeometryEdit;

    let drawMode;
    if (mode === 'polygon') {
        drawMode = 'draw_polygon_with_angle_snap';
    } else if (mode === 'line') {
        drawMode = 'draw_line_string_with_angle_snap';
    } else {
        drawMode = 'draw_point_custom';
    }
    drawRef.current.changeMode(drawMode, defaultProperties || {});
  }, []);

  const resetDrawing = useCallback(() => {
    if (!drawRef.current) return;
    drawRef.current.deleteAll();
    if (currentDrawModeRef.current) {
      const { mode, defaultProperties } = currentDrawModeRef.current;
      startDrawing(mode, defaultProperties);
    }
  }, [startDrawing]);

  const completeDrawing = useCallback(() => {
    if (!drawRef.current) return;
    const inst = currentDrawModeInstanceRef.current;
    if (inst) {
      const { type, ctx, state } = inst;
      if (type === 'polygon' && state.polygon && state.polygon.isValid()) {
        ctx.changeMode('simple_select', { featureIds: [state.polygon.id] });
        return;
      }
      if (type === 'line' && state.line && state.line.isValid()) {
        ctx.changeMode('simple_select', { featureIds: [state.line.id] });
        return;
      }
    }
    try {
      const all = drawRef.current.getAll();
      if (all && all.features && all.features.length > 0) {
        const feat = all.features[0];
        drawRef.current.changeMode('simple_select', { featureIds: [feat.id] });
      }
    } catch (e) {
      console.warn('completeDrawing error:', e);
    }
  }, []);

  const setOnDrawComplete = useCallback((callback) => {
    onDrawCompleteCallbackRef.current = callback;
  }, []);

  const stopDrawing = useCallback(() => {
    if (!drawRef.current) return;
    drawRef.current.changeMode('simple_select');
  }, []);

  const clearDrawing = useCallback(() => {
    if (drawRef.current) {
      drawRef.current.deleteAll();
    }
  }, []);

  const cleanup = useCallback(() => {
    try {
      if (map && drawRef.current && map.hasControl && map.hasControl(drawRef.current) && map.style) {
        map.removeControl(drawRef.current);
      }
    } catch (err) {
      console.warn('MapboxDraw cleanup warning:', err);
    }
  }, [map]);

  const startEditing = useCallback((feature) => {
      if (!drawRef.current || !feature) return;
      drawRef.current.deleteAll();
      isGeometryEditRef.current = true;
      editingFeatureTargetRef.current = feature;

      // Инициализируем историю снимков для отката вертексов
      if (feature.geometry && feature.geometry.coordinates) {
        vertexHistoryRef.current = [JSON.parse(JSON.stringify(feature.geometry.coordinates))];
        vertexHistoryIndexRef.current = 0;
      }

      const featureToLoad = {
          type: 'Feature',
          id: feature.id,
          geometry: feature.geometry,
          properties: feature.properties || {}
      };

      try {
          drawRef.current.add(featureToLoad);
          setTimeout(() => {
              if (drawRef.current) {
                  drawRef.current.changeMode('direct_select', { featureId: feature.id });
              }
          }, 30);
      } catch (err) {
          console.error('Error starting direct_select:', err);
      }
  }, []);

  const clearAndRedraw = useCallback(() => {
      if (!drawRef.current) return;
      drawRef.current.deleteAll();
      vertexHistoryRef.current = [];
      vertexHistoryIndexRef.current = -1;
      isGeometryEditRef.current = true;

      const targetFeature = editingFeatureTargetRef.current;
      const geomType = targetFeature?.geometry?.type || 'Polygon';
      const isLine = geomType === 'LineString' || geomType === 'MultiLineString' || targetFeature?.properties?.type === 'line' || targetFeature?.properties?.type === 'road';
      const isPoint = geomType === 'Point' || geomType === 'MultiPoint' || targetFeature?.properties?.type === 'marker';

      let drawMode = 'draw_polygon_with_angle_snap';
      if (isLine) drawMode = 'draw_line_string_with_angle_snap';
      else if (isPoint) drawMode = 'draw_point_custom';

      drawRef.current.changeMode(drawMode, targetFeature?.properties || {});
  }, []);

  const saveEditing = useCallback(() => {
      if (!drawRef.current) return;
      const all = drawRef.current.getAll();
      if (all && all.features && all.features.length > 0) {
          const updated = all.features[0];
          if (onFeatureUpdateRef.current) {
              onFeatureUpdateRef.current(updated);
          }
      }
      isGeometryEditRef.current = false;
      editingFeatureTargetRef.current = null;
      vertexHistoryRef.current = [];
      vertexHistoryIndexRef.current = -1;
      drawRef.current.changeMode('simple_select', {});
      drawRef.current.deleteAll();
  }, []);

  const stopEditing = useCallback(() => {
      if (!drawRef.current) return;
      isGeometryEditRef.current = false;
      editingFeatureTargetRef.current = null;
      vertexHistoryRef.current = [];
      vertexHistoryIndexRef.current = -1;
      drawRef.current.changeMode('simple_select', {});
      drawRef.current.deleteAll();
  }, []);

  return { 
    startDrawing, 
    stopDrawing, 
    cleanup, 
    setOnDrawComplete, 
    clearDrawing, 
    startEditing, 
    saveEditing, 
    stopEditing, 
    undoVertex, 
    redoVertex, 
    clearAndRedraw,
    undoLastPoint,
    resetDrawing,
    completeDrawing
  };
};